/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Stack } from "@mui/material";
import { useEffect, useState, useRef } from "react";
import { useWatch } from "react-hook-form";

import {
  FormButtonGroup,
  FormSelectInput,
  FormTimeInput,
} from "../../../../components/form";
import { GetSchedule, GetSlot } from "../../../../services/fhir";
import { useFhirData } from "../../../../utils/useFhirData";

import dayjs from "dayjs";
import { toast } from "react-toastify";
import { oids } from "../../../../configs/Settings";
import { useOrganizationContext } from "../../../../contexts/OrganizationContext";
import { usePractitionerRoleContext } from "../../../../contexts/PractitionerRoleContext";
import { useSessionContext } from "../../../../contexts/SessionContext";
import {
  practitionerRefsSharingAssociationCnpj,
  scheduleTouchesAllowedPractitioners,
} from "@/utils/practitionerRoleAssociationScope";
import { useFormatter } from "@/utils/useFormatter";
import { useDatetime } from "../../../../utils/useDatetime";
import { EVOLUTION_TYPE_SERVICE } from "@/components/schedule/evolution/constants";

/**
 * O Appointment exige Location (AuxiliarData lê locationActor.reference).
 * Agendas de teleconsultoria (criadas sem Location) e agendas legadas sem local
 * não conseguem gerar agendamento: não são oferecidas.
 */
const hasLocation = (entry: any) =>
  Boolean(useFhirData.findActor(entry?.resource?.actor, "Location")?.reference);

export function SelectAppointmentActors({
  control,
  setModalOpen,
  handleSubmit,
  setValue,
  navigateToNextPage,
  setScheduleActors,
  setData,
  appointmentType,
}: any) {
  const { access, user } = useSessionContext();
  const { practitioner, practitionerRole } = usePractitionerRoleContext();
  const [loading, setLoading] = useState<boolean>(false);
  const { organization } = useOrganizationContext();
  const { formatDateToISO } = useDatetime;
  const auxActor =
    !access?.roles?.includes("manager") &&
    `Practitioner/${oids.cpf}-${user.username}`;

  const selectedPractitioner = useWatch({ name: "practitioner", control });
  const selectedScheduleId = useWatch({ name: "schedule", control });
  const initialLoadDone = useRef(false);

  const [schedule, setSchedule] = useState<any>();
  const [availableSlots, setAvailableSlots] = useState<any[]>([]);
  const [slotsLoading, setSlotsLoading] = useState<boolean>(false);
  const [defaultScheduleId, setDefaultScheduleId] = useState<string | null>(
    null
  );

  const isEncaixe = appointmentType === "WALKIN";
  const isEvolution = appointmentType === EVOLUTION_TYPE_SERVICE;
  // Evolução sem vagas livres: informa o horário como no encaixe.
  const [evolutionUsesCustomTime, setEvolutionUsesCustomTime] =
    useState(false);
  const useCustomTime = isEncaixe || evolutionUsesCustomTime;

  const scheduleOptions = (() => {
    const allowed = practitionerRefsSharingAssociationCnpj(
      practitionerRole,
      access?.managerAssociationScopeCnpjDigits,
    );
    const scopeDigits = access?.managerAssociationScopeCnpjDigits;
    return schedule?.filter((sc: any) => {
      const practRef = useFhirData.findActor(
        sc.resource.actor,
        "Practitioner",
      )?.reference;
      if (scopeDigits && allowed.size > 0 && practRef && !allowed.has(practRef)) {
        return false;
      }
      return practRef?.includes(
        auxActor ? user?.username : selectedPractitioner || user?.username,
      );
    });
  })();

  const onSubmit = async (data: any) => {
    try {
      const selectedSchedule = scheduleOptions?.find(
        (e: any) => e.resource?.id === data?.schedule
      );

      setScheduleActors(selectedSchedule?.resource?.actor);

      if (!selectedSchedule) {
        toast.warn("É necessário selecionar uma agenda!");
        throw new Error("");
      } else {
        setValue("scheduleCompleted", selectedSchedule?.resource);
      }

      // Proteção extra: sem Location o Appointment quebra antes do POST.
      if (!hasLocation(selectedSchedule)) {
        toast.warn("Esta agenda não tem local configurado. Escolha outra agenda.");
        throw new Error("");
      }

      // Detecta se a agenda selecionada é de atendimento a domicílio.
      // A extension schedule-attendance-type guarda o canal da agenda.
      const isDomicilio = selectedSchedule?.resource?.extension?.some(
        (ext: any) =>
          ext.url.includes("schedule-attendance-type") &&
          ext.valueCode === "domicilio"
      ) ?? false;

      if (isEncaixe || evolutionUsesCustomTime) {
        if (!data.timeStart) {
          toast.warn("É necessário selecionar um horário!");
          throw new Error("");
        }

        let slotDuration = 30;

        if (selectedSchedule?.resource?.extension) {
          const durationExtension = selectedSchedule.resource.extension.find(
            (ext: any) => ext.url.includes("slot-duration")
          );

          if (durationExtension?.valueDuration?.value) {
            slotDuration = durationExtension.valueDuration.value;
          }
        }

        setValue("timeEnd", dayjs(data.timeStart).add(slotDuration, "minutes"));

        const timeEnd = dayjs(data.timeStart).add(slotDuration, "minutes");
        setValue("slotInfo", {
          start: data.timeStart,
          end: timeEnd,
        });

        setValue("auxUpdateSlot", {});
      } else {
        const selectedSlot = availableSlots.find(
          (slot) => slot.value === data.timeStart
        );
        if (!selectedSlot) {
          toast.warn("É necessário selecionar um horário!");
          throw new Error("");
        }

        setValue("timeEnd", dayjs(selectedSlot.end));
        setValue("timeStart", dayjs(selectedSlot.start));

        setValue("slotInfo", {
          id: selectedSlot.value,
          start: selectedSlot.start,
          end: selectedSlot.end,
        });

        setValue("auxUpdateSlot", {
          id: selectedSlot.value,
        });

        setValue("scheduleCompleted", selectedSchedule?.resource);

        setData({
          id: selectedSlot.value,
          auxUpdateSlot: {
            id: selectedSlot.value,
          },
          slotTime: {
            start: selectedSlot.start,
            end: selectedSlot.end,
          },
        });
      }

      const serviceCost = selectedSchedule?.resource?.extension?.find(
        (el: any) =>
          el?.url ===
          "https://fluxmed.com.br/fhir/StructureDefinition/service-cost"
      )?.valueDecimal;

      if (serviceCost) {
        setValue("serviceCost", useFormatter.formatCurrency(serviceCost * 100));
        setValue(
          "originalServiceCost",
          useFormatter.formatCurrency(serviceCost * 100)
        );
      }

      // Passa isDomicilio para o pai decidir qual step exibir
      navigateToNextPage(isDomicilio);
    } catch (err) {
      console.log("Erro", err);
    }
  };

  useEffect(() => {
    async function fetchSchedules() {
      setLoading(true);
      try {
        const schedules = await GetSchedule({
          active: true,
          organization: organization?.id,
          ...(auxActor && { actor: auxActor }),
        });

        if (schedules?.entry) {
          const scopeDigits = access?.managerAssociationScopeCnpjDigits;
          const allowed = practitionerRefsSharingAssociationCnpj(
            practitionerRole,
            scopeDigits,
          );
          // Só agendas com local (ver hasLocation).
          let entries = schedules.entry.filter(hasLocation);
          if (scopeDigits && allowed.size > 0) {
            entries = entries.filter((e: any) =>
              scheduleTouchesAllowedPractitioners(e?.resource, allowed),
            );
          }
          setSchedule(entries);

          // Default a partir da lista filtrada: nunca pré-seleciona agenda sem local.
          const defaultSchedule = entries.find((e: any) =>
            useFhirData
              .findActor(e?.resource?.actor, "Practitioner")
              ?.reference.includes(user?.username)
          );

          if (defaultSchedule) {
            const scheduleId = defaultSchedule.resource?.id;
            setDefaultScheduleId(scheduleId);

            if (!selectedScheduleId) {
              setValue("schedule", scheduleId);
            }
          }
        } else {
          console.log("[SelectAppointmentActors] Nenhuma agenda encontrada");
        }
      } catch (err) {
        toast.error("Erro ao buscar agendas");
      } finally {
        setLoading(false);
        initialLoadDone.current = true;
      }
    }
    fetchSchedules();
  }, [
    access?.managerAssociationScopeCnpjDigits,
    access?.roles,
    practitionerRole,
  ]);

  const fetchSlots = async (scheduleId: string) => {
    if (isEncaixe) return;

    if (!scheduleId) {
      return;
    }

    setSlotsLoading(true);
    setEvolutionUsesCustomTime(false);

    const handleNoSlots = () => {
      setAvailableSlots([]);
      if (isEvolution) {
        // Sem vaga livre: secretária informa o horário (mesmo fluxo do encaixe).
        setEvolutionUsesCustomTime(true);
        setValue("timeStart", dayjs());
        return;
      }
      toast.info("Não há horários disponíveis para esta agenda");
    };

    try {
      const today = dayjs();
      const startDate = formatDateToISO(today);
      const endDate = formatDateToISO(today);

      const slotResponse = await GetSlot({
        schedule: scheduleId,
        status: ["free"],
        sort: "start",
        startDate,
        endDate,
      });

      if (
        !slotResponse ||
        !slotResponse.entry ||
        slotResponse.entry.length === 0
      ) {
        handleNoSlots();
        return;
      }

      const currentTime = dayjs();
      const filteredSlots = slotResponse.entry.filter((slot: any) => {
        const isAfter =
          slot.resource && dayjs(slot.resource.end).isAfter(currentTime);
        return isAfter;
      });

      const { formatDateTimeToHour } = useFormatter;
      const options = filteredSlots.map((slot: any) => ({
        value: slot.resource.id,
        display: formatDateTimeToHour(slot.resource.start),
        start: slot.resource.start,
        end: slot.resource.end,
      }));

      options.sort((a: any, b: any) => {
        const timeA = dayjs(a.start);
        const timeB = dayjs(b.start);
        return timeA.isAfter(timeB) ? 1 : -1;
      });

      if (options.length === 0) {
        handleNoSlots();
        return;
      }

      setValue("timeStart", undefined);
      setAvailableSlots(options);
    } catch (error) {
      console.error("Erro ao buscar slots:", error);
      if (isEvolution) {
        handleNoSlots();
      } else {
        toast.error("Erro ao buscar horários disponíveis");
        setAvailableSlots([]);
      }
    } finally {
      setSlotsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedScheduleId) {
      fetchSlots(selectedScheduleId);
    } else {
      setAvailableSlots([]);
    }
  }, [selectedScheduleId]);

  useEffect(() => {
    if (useCustomTime) return;

    if (defaultScheduleId && initialLoadDone.current) {
      if (!selectedScheduleId || selectedScheduleId === defaultScheduleId) {
        fetchSlots(defaultScheduleId);
      }
    }
  }, [defaultScheduleId, initialLoadDone.current]);

  return (
    <Stack spacing={2}>
      {loading && <Box p={1}>Carregando agendas...</Box>}
      {!useCustomTime && slotsLoading && <Box p={1}>Carregando horários...</Box>}

      <Box p={1}>
        <FormSelectInput
          name="practitioner"
          defaultValue={
            practitioner?.find((e) => e.resource?.id?.includes(user.username))
              ?.resource?.id || ""
          }
          control={control}
          label="Profissional"
          selectOptions={
            practitioner
              ?.filter((prac) => {
                if (auxActor) {
                  return prac?.resource?.id?.includes(user?.username);
                }
                const scopeDigits = access?.managerAssociationScopeCnpjDigits;
                const allowed = practitionerRefsSharingAssociationCnpj(
                  practitionerRole,
                  scopeDigits,
                );
                if (scopeDigits && allowed.size > 0) {
                  const pid = prac?.resource?.id?.startsWith("Practitioner/")
                    ? prac.resource.id
                    : `Practitioner/${prac?.resource?.id}`;
                  return allowed.has(pid);
                }
                return true;
              })
              ?.map((e) => {
                return {
                  display: e?.resource?.name?.[0]?.text,
                  value: e?.resource?.id,
                };
              }) || []
          }
          required
        />
      </Box>

      <Box p={1}>
        {schedule && (
          <FormSelectInput
            name="schedule"
            control={control}
            label="Agenda"
            defaultValue={
              schedule?.find((e: any) =>
                useFhirData
                  .findActor(e?.resource?.actor, "Practitioner")
                  ?.reference.includes(user?.username)
              )?.resource?.id
            }
            selectOptions={
              scheduleOptions?.map((e: any) => {
                return {
                  display:
                    `${e?.resource.name} - ${
                      useFhirData.findActor(
                        e?.resource?.actor,
                        "HealthcareService"
                      )?.display
                    }` || "",
                  value: e.resource?.id || "",
                };
              }) || []
            }
            required
          />
        )}
      </Box>

      <Box p={1}>
        {useCustomTime ? (
          <FormTimeInput
            required
            name="timeStart"
            label={
              evolutionUsesCustomTime
                ? "Horário da evolução"
                : "Horário de encaixe"
            }
            control={control}
            defaultValue={dayjs()}
            validate={(e: any) => !isNaN(e) || e === null}
          />
        ) : (
          <FormSelectInput
            required
            name="timeStart"
            label="Horário"
            control={control}
            selectOptions={availableSlots}
            readOnly={!selectedScheduleId || slotsLoading}
          />
        )}
      </Box>

      <FormButtonGroup
        goBackButtonText="Fechar"
        onGoBackButtonClick={() => {
          setModalOpen(false);
        }}
        onNextButtonClick={handleSubmit(onSubmit)}
        loading={loading || (!useCustomTime && slotsLoading)}
      />
    </Stack>
  );
}
